import { motion } from "framer-motion";
import { ArrowRight, Play, Mic2, BadgeCheck } from "lucide-react";

const HOST_IMG = "/images/host-portrait.jpg";

const STATS = [
  { value: "45K+", label: "Verified Investor Network" },
  { value: "100+", label: "Ventures & Mentees" },
  { value: "1:1", label: "Unscripted Conversations" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
  return (
    <section id="top" className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 overflow-hidden">
      <div className="ambient-glow w-[320px] h-[320px] sm:w-[520px] sm:h-[520px] bg-amber-500/15 -top-32 sm:-top-40 right-[-160px] sm:right-[-120px]" />
      <div className="ambient-glow w-[300px] h-[300px] sm:w-[420px] sm:h-[420px] bg-sky-500/10 top-64 left-[-180px] sm:left-[-160px]" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div {...fadeUp(0)}>
            <span className="inline-flex max-w-full items-center gap-2 clay-badge rounded-3xl sm:rounded-full px-4 sm:px-5 py-2 text-[10px] sm:text-[11px] leading-relaxed font-semibold tracking-[0.16em] sm:tracking-[0.22em] uppercase text-amber-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" />
              An Exclusive Podcast Series — Hosted by Ankush Mehrotra
            </span>
          </motion.div>

          <motion.h1
            {...fadeUp(0.12)}
            data-testid="hero-heading"
            className="mt-7 sm:mt-8 font-display text-3xl min-[375px]:text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
          >
            Decoding <span className="text-gold-gradient">Income Quotients.</span>
            <br className="hidden sm:block" />
            Unlocking Mindsets.
          </motion.h1>

          <motion.p {...fadeUp(0.24)} className="mt-5 sm:mt-7 max-w-xl text-sm sm:text-lg text-slate-400 leading-relaxed">
            MyIQ Talks is where entrepreneurs, leaders, artists and gurus sit down for
            open-ended, unscripted conversations about money, growth, and the human
            stories behind the numbers. No scripts. No clickbait. Just candour.
          </motion.p>

          <motion.div {...fadeUp(0.36)} className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
            <a
              href="#request-slot"
              data-testid="hero-cta-book-slot"
              className="tactile-btn inline-flex items-center justify-center gap-2 rounded-full w-full sm:w-auto px-6 sm:px-8 py-4 text-sm font-bold"
            >
              Request a Guest Slot <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#guest-dimensions"
              data-testid="hero-cta-explore-topics"
              className="ghost-btn inline-flex items-center justify-center gap-2 rounded-full w-full sm:w-auto px-6 sm:px-8 py-4 text-sm font-semibold text-slate-200"
            >
              <Play className="w-4 h-4 text-amber-400" /> Listen to Teasers
            </a>
          </motion.div>

          <motion.div {...fadeUp(0.48)} className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg">
            {STATS.map((s) => (
              <div key={s.label} className="glass-panel rounded-2xl px-4 py-4 sm:py-5">
                <p className="font-display text-2xl sm:text-3xl font-extrabold text-gold-gradient">{s.value}</p>
                <p className="mt-1 text-[11px] sm:text-xs text-slate-500 leading-snug">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative mt-2 sm:mt-4 lg:mt-0"
        >
          <div className="ambient-glow w-56 h-56 sm:w-72 sm:h-72 bg-amber-500/25 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

          <div data-testid="hero-host-intro-card" className="clay-card relative p-3 sm:p-4 float-slow">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={HOST_IMG}
                alt="Ankush Mehrotra — host of MyIQ Talks"
                className="w-full h-[320px] min-[390px]:h-[360px] sm:h-[420px] object-cover object-[62%_22%]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent opacity-90" />
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 glass-panel rounded-2xl px-4 sm:px-5 py-3 sm:py-4 flex flex-col min-[420px]:flex-row min-[420px]:items-center gap-3 justify-between">
                <div>
                  <p className="font-display font-bold text-slate-100 flex items-center gap-1.5">
                    Ankush Mehrotra <BadgeCheck className="w-4 h-4 text-amber-400" />
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Host · Investor · Author · Speaker</p>
                </div>
                <span className="clay-badge self-start min-[420px]:self-auto rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-amber-300">
                  Your Host
                </span>
              </div>
            </div>

            <div className="mt-3 sm:mt-4 flex flex-col min-[390px]:flex-row min-[390px]:items-center gap-3 min-[390px]:justify-between px-2 sm:px-3 pb-2">
              <div className="flex items-center gap-2 text-slate-400">
                <Mic2 className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-mono-iq tracking-wider">EP.00 — TEASER</span>
              </div>
              <div className="flex items-end gap-1 h-6">
                {[0.9, 0.4, 1, 0.6, 0.8, 0.35, 0.95, 0.55, 0.75, 0.45].map((d, i) => (
                  <span
                    key={i}
                    className="eq-bar w-1 rounded-full bg-gradient-to-t from-amber-600 to-amber-300"
                    style={{ height: "100%", animationDelay: `${i * 0.12}s`, animationDuration: `${0.9 + d * 0.5}s` }}
                  />
                ))}
              </div>
            </div>
          </div>

          <span className="hidden sm:block float-slower absolute -top-6 -left-6 clay-badge rounded-full px-5 py-2.5 text-[11px] font-bold tracking-[0.2em] uppercase text-slate-300 select-none">
            #KeepRoaring
          </span>
          <span className="hidden sm:block float-slow absolute -bottom-5 -right-3 clay-badge rounded-full px-5 py-2.5 text-[11px] font-bold tracking-[0.2em] uppercase text-amber-300 select-none">
            #KeepGrowing
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
